import type { GoogleGeocoderResult, GooglePlaceResult, PageableContent, PageableContentAPI } from '@cobliteam/common-types';

import { driverGraphQLAPIToDriver } from '../../driver/APITransformer';
import { proofOfConclusionTransformer } from '../../proofOfConclusion';
import {
  GeocoderAddressComponentTypes,
  type InputActivity,
  type NewActivity,
  type NewActivityAPI,
  type NewDestination,
  type NewDestinationAPI,
  type ReportActivity,
  type ReportActivityAPI,
} from '../Definitions';

const destinationTransformer = (destinationAPI: NewDestinationAPI): NewDestination => {
  const {
    city,
    coordinate,
    country,
    neighborhood,
    postal_code: postalCode,
    state,
    street_address: streetAddress,
    street_complement: streetComplement,
    street_number: streetNumber,
  } = destinationAPI;

  return {
    coordinate,
    ...(city && { city }),
    ...(city && { country }),
    ...(city && { neighborhood }),
    ...(city && { state }),
    ...(postalCode && { postalCode }),
    ...(streetAddress && { streetAddress }),
    ...(streetComplement && { streetComplement }),
    ...(streetNumber && { streetNumber }),
  };
};

export const reportActivityTransformer = (reportActivityAPI: ReportActivityAPI): ReportActivity => {
  const {
    id,
    type,
    name,
    duration,
    destination,
    phone_number: phoneNumber,
    instructions,
    observations,
    completion_status: completionStatus,
    end_time: endTime,
    start_time: startTime,
    time_windows: timeWindows,
    driver,
    vehicle,
  } = reportActivityAPI;

  return {
    id,
    type,
    duration,
    destination: destinationTransformer(destination),
    endTime,
    startTime,
    driver: driver ? driverGraphQLAPIToDriver(driver) : undefined,
    vehicle,
    ...(completionStatus && { completionStatus }),
    ...(observations && { observations }),
    ...(instructions && { instructions }),
    ...(timeWindows && { timeWindows }),
    ...(name && { name }),
    ...(phoneNumber && { phoneNumber }),
  };
};

export const activityTransformer = (activityAPI: NewActivityAPI): NewActivity => {
  const {
    id,
    name,
    type,
    visit_status: visitStatus,
    completion_status: completionStatus,
    position,
    destination,
    planned_duration: plannedDuration,
    planned_distance_in_meters: plannedDistanceInMeters,
    created_at: createdAt,
    updated_at: updatedAt,
    time_windows: timeWindows,
    waiting_time: waitingTime,
    start_time: startTime,
    end_time: endTime,
    additional_info: additionalInfo,
    arrival_time: arrivalTime,
    executed_duration: executedDuration,
    proof_of_conclusion: proofOfConclusion,
    executed_end_time: executedEndTime,
    phone_number: phoneNumber,
    load_size: loadSize,
    device_id: deviceId,
    driver_id: driverId,
    driver_name: driverName,
    route_name: routeName,
    route_id: routeId,
    vehicle_id: vehicleId,
  } = activityAPI;

  return {
    id,
    type,
    visitStatus,
    completionStatus,
    position,
    destination: destinationTransformer(destination),
    createdAt,
    updatedAt,
    name,
    ...(routeName && { routeName }),
    ...(deviceId && { deviceId }),
    ...(driverId && { driverId }),
    ...(driverName && { driverName }),
    ...(routeId && { routeId }),
    ...(vehicleId && { vehicleId }),
    ...(plannedDuration && { plannedDuration }),
    ...(plannedDistanceInMeters && { plannedDistanceInMeters }),
    ...(timeWindows && { timeWindows }),
    ...(waitingTime && { waitingTime }),
    ...(startTime && { startTime }),
    ...(endTime && { endTime }),
    ...(additionalInfo && { additionalInfo }),
    ...(arrivalTime && { arrivalTime }),
    ...(executedDuration && { executedDuration }),
    ...(executedEndTime && { executedEndTime }),
    proofOfConclusion: proofOfConclusion ? proofOfConclusionTransformer(proofOfConclusion) : undefined,
    phoneNumber,
    loadSize,
  };
};

export const activitiesPageableTransformer = (page: PageableContentAPI<NewActivityAPI>): PageableContent<NewActivity> => ({
  content: page.content.map(activityTransformer),
  last: page.last,
  totalPages: page.total_pages,
  totalElements: page.total_elements,
  numberOfElements: page.number_of_elements,
  number: page.number,
  size: page.size,
  first: page.first,
  empty: page.empty,
});

export const placeFromGeocoderResultTransformer = (geocoderResult: GoogleGeocoderResult): GooglePlaceResult => {
  const streetAddress = geocoderResult.address_components.find((component) => component.types.includes(GeocoderAddressComponentTypes.STREET_ADDRESS));
  return {
    address_components: geocoderResult.address_components,
    formatted_address: geocoderResult.formatted_address,
    geometry: geocoderResult.geometry,
    name: streetAddress ? streetAddress.short_name : geocoderResult.formatted_address,
  };
};

export const loadedAddressPlaceResultTransformer = (data: GoogleGeocoderResult): GooglePlaceResult => {
  const { address_components: addressComponents, formatted_address: formattedAddress, geometry } = data;

  return {
    name: formattedAddress,
    address_components: addressComponents,
    formatted_address: formattedAddress,
    geometry,
  };
};

export const loadedAddressInputActivityTransformer = (geocoderResult: GoogleGeocoderResult): InputActivity => ({
  destination: loadedAddressPlaceResultTransformer(geocoderResult),
});
